import {
  List,
  ListItem,
  ListItemText,
  Theme,
  withStyles,
  WithStyles
} from '@material-ui/core'
import i18next, { TFunction } from 'i18next'
import { FC, Fragment } from 'react'
import { getFirstCharCapitalized } from '../../../libs/strings'
import { MuiClasses } from '../../../types'
import { formatStringIfDate } from '../../../utils/dates'
import { getEnergyCertificationDisplayLabel } from '../../../utils/validation/energyCertifications'

const styles = ({ breakpoints }: Theme) => ({
  listItem: {
    padding: '8px 0'
  },
  listItems: {
    padding: '0',
    margin: '0',
    color: '#000000DE'
  },
  listItemsTitle: {
    padding: '0',
    margin: '0',
    fontSize: '1rem',
    fontWeight: 400,
    fontFamily: 'CircularBold',
    color: '#000000DE'
  },
  spouseLifeSituation: {
    '& span': {
      fontSize: '1.2rem'
    }
  },
  listRow: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr 260px',
    alignItems: 'baseline',
    width: '100%',
    [breakpoints.down('sm')]: {
      gridTemplateColumns: 'auto 1fr 200px'
    },
    [breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr'
    }
  },
  listLabelText: {
    marginTop: 0,
    marginBottom: 0
  },
  listLeader: {
    width: '100%',
    borderBottom: '1px dotted #c7c7c7',
    height: '1px',
    margin: '0 8px',
    [breakpoints.down('xs')]: {
      display: 'none'
    }
  },
  listValue: {
    textAlign: 'left' as const,
    width: '100%',
    paddingLeft: '16px',
    [breakpoints.down('xs')]: {
      paddingLeft: '0'
    }
  },
})

export interface CustomListProps extends WithStyles {
  classes: MuiClasses
  TitleTypography?: JSX.Element
  data: any
  informationOrder: string[]
  translationPath: string
  t: i18next.TFunction
  multiSelectPaths?: { [key: string]: string }
  hideListItemLogic?: (key: string, value: string) => boolean
  capitalizeFirstLetter?: boolean
}

interface ListItemProps {
  t: TFunction
  objectValue: string
  capitalizeFirstLetter?: boolean
}

interface MultiSelectTranslationProps extends ListItemProps {
  multiSelectPath: string
}

const getTranslatedMultiValue = ({
  t,
  multiSelectPath,
  objectValue,
  capitalizeFirstLetter
}: MultiSelectTranslationProps) => {
  const translatedValue = t(`${multiSelectPath}.${objectValue}`)
  return capitalizeFirstLetter
    ? getFirstCharCapitalized(translatedValue)
    : translatedValue
}

const getCapitalized = ({
  t,
  objectValue,
  capitalizeFirstLetter
}: ListItemProps) => {
  const translatedValue = t(`${formatStringIfDate(objectValue)}`)
  return capitalizeFirstLetter
    ? getFirstCharCapitalized(translatedValue)
    : translatedValue
}

const CustomList: FC<CustomListProps> = ({
  classes,
  t,
  TitleTypography,
  data,
  informationOrder,
  translationPath,
  multiSelectPaths,
  hideListItemLogic,
  capitalizeFirstLetter
}) => {
  const renderSwitch = ({
    key,
    objectValue,
    multiSelectPath,
    isErrorOrNoAnswer
  }: any) => {
    switch (key) {
      case 'phoneNumber':
        return (
          <a className={classes.valueLink} href={`tel:+${objectValue}`}>
            +{objectValue}
          </a>
        )
      case 'pets':
        return (
          <ListItemText
            primary={getCapitalized({
              t,
              objectValue,
              capitalizeFirstLetter
            })}
            className={classes.listItems}
          />
        )
      default: {
        const displayValue =
          key === 'energyCertification' && typeof objectValue === 'string'
            ? getEnergyCertificationDisplayLabel(objectValue) || objectValue
            : objectValue

        return (
          <>
            {multiSelectPath && !isErrorOrNoAnswer ? (
              <ListItemText
                primary={getTranslatedMultiValue({
                  t,
                  objectValue,
                  capitalizeFirstLetter,
                  multiSelectPath
                })}
                className={classes.listItems}
              />
            ) : (
              <ListItemText
                primary={getCapitalized({
                  t,
                  objectValue: displayValue,
                  capitalizeFirstLetter
                })}
                className={classes.listItems}
              />
            )}
          </>
        )
      }
    }
  }
  return (
    <Fragment>
      {TitleTypography}
      <List
        component={'div' as any}
        disablePadding
        className={classes.innerList}
      >
        {informationOrder.map((key: string, index: number) => {
          const isErrorOrNoAnswer = hideListItemLogic
            ? data[key] === 'error' || hideListItemLogic(key, data[key])
            : data[key] === 'error'
          let objectValue = isErrorOrNoAnswer ? undefined : data[key]
          const multiSelectPath = multiSelectPaths && multiSelectPaths[key]
          let newPath = ''
          if (
            (key === 'livelihood' || key === 'spouseLivelihood') &&
            Array.isArray(data[key])
          ) {
            newPath = `newApplication.livelihood`
            objectValue = data[key]
              .map((value: string) =>
                value === 'myAnswer'
                  ? data['livelihoodCustom']
                  : t(`newApplication.livelihoodOptions.${value}`)
              )
              .join(', ')
          }
          if (key === 'movingAbout') {
            newPath = 'application.list.readyForMoveDate'
          }
          if (key === 'energyCertification') {
            newPath = `${translationPath}energyCertificationLabel`
          }
          if (key === 'availableFromDate') {
            newPath = 'apartment.availableFromDate'
          }
          if (key === 'districtHeatingCheckBoxProperty') {
            newPath = 'apartment.heating.district'
          }
          if (key === 'electricHeatingCheckBoxProperty') {
            newPath = 'apartment.heating.electric'
          }
          if (
            key === 'lifeSituationCustom' ||
            key === 'spouseLifeSituationCustom' ||
            key === 'spouseLifeSituation'
          ) {
            newPath = `application.list.lifeSituation`
          }

          if (key === 'depositPaymentMethodCustom') {
            newPath = 'application.list.depositPaymentMethod'
          }

          if (objectValue === 'nextMonth') {
            return <></>
          }

          if (key === 'spouseCreditStatus') {
            newPath = 'application.list.creditStatus'
          }
          return (
            objectValue && (
              <>
                {key === 'spouseLifeSituation' && (
                  <ListItemText
                    primary={t('application.suposeTitle')}
                    className={classes.spouseLifeSituation}
                    style={{
                      margin: '10px 0'
                    }}
                  />
                )}
                <ListItem key={`${key}+${index}`} className={classes.listItem}>
                  <div className={classes.listRow}>
                    <div className={classes.listLabel}>
                      <ListItemText
                        secondary={
                          newPath !== ''
                            ? t(`${newPath}`)
                            : t(`${translationPath}${key}`)
                        }
                        classes={{
                          secondary: classes.listItemsTitle,
                          root: classes.listLabelText
                        }}
                      />
                    </div>
                    <div className={classes.listLeader} aria-hidden='true' />
                    <div className={classes.listValue}>
                      {renderSwitch({
                        key,
                        objectValue,
                        multiSelectPath,
                        isErrorOrNoAnswer
                      })}
                    </div>
                  </div>
                </ListItem>
              </>
            )
          )
        })}
      </List>
    </Fragment>
  )
}

export default withStyles(styles)(CustomList)
